import React, { MutableRefObject, useCallback, useRef } from 'react';

import type { InputRef } from 'antd';

/** Столько цифр остаётся в российском номере без кода страны. */
const PHONE_DIGITS_WITHOUT_COUNTRY_CODE = 10;

/** Первая цифра российского номера, записанного целиком: +7…, 7… или 8…. */
const RUSSIAN_COUNTRY_CODES = ['7', '8'];

/**
 * Готовит вставляемый текст для поля, которое ждёт 10 цифр номера без кода страны.
 *
 * Из буфера номер обычно копируется целиком — `+7 (916) 123-45-67`, `79161234567`,
 * `89161234567`. Маска берёт первые 10 цифр подряд, поэтому без обработки код страны
 * занимает место первых цифр номера: из `+79161234567` получается `(791)-612-34-56` —
 * другой номер, но выглядит валидным.
 *
 * Код срезаем только когда цифр ровно 11 и первая из них российская. Номера с чужим
 * кодом страны не трогаем: поле рассчитано на Россию, остальное обрабатывается как прежде.
 */
export const prepareRussianPhoneInput = (value: string): string => {
    const digits = value.replace(/\D/g, '');

    const isWholeRussianNumber =
        digits.length === PHONE_DIGITS_WITHOUT_COUNTRY_CODE + 1 &&
        RUSSIAN_COUNTRY_CODES.includes(digits[0]);

    return isWholeRussianNumber ? digits.slice(1) : digits;
};

/**
 * Номер цифрами с кодом страны — для ссылок `tel:`, `sms:` и `wa.me`.
 *
 * Поля телефона стоят под маской `+{7} (000) 000-00-00`, где семёрка —
 * фиксированный символ: в сохранённое значение она не попадает, и наружу
 * уходят только десять цифр. Для звонка и сообщения этого мало — набирается
 * неполный номер, а WhatsApp такой вовсе не находит.
 *
 * Номера с чужим кодом страны и прочий ввод не трогаем: приписать им семёрку
 * было бы хуже, чем оставить как есть.
 */
export const toRussianPhoneDigits = (value: string | number): string => {
    const digits = String(value ?? '').replace(/\D/g, '');

    if (digits.length === PHONE_DIGITS_WITHOUT_COUNTRY_CODE) return `7${digits}`;

    const isWholeRussianNumber =
        digits.length === PHONE_DIGITS_WITHOUT_COUNTRY_CODE + 1 &&
        RUSSIAN_COUNTRY_CODES.includes(digits[0]);

    return isWholeRussianNumber ? `7${digits.slice(1)}` : digits;
};

/**
 * Номер для показа: `+7 (916) 123-45-67`.
 *
 * Бэк отдаёт номер как получится — десять цифр, с семёркой, с восьмёркой или с
 * плюсом. Приводим к одному виду через `toRussianPhoneDigits`; если номер не
 * российский или неполный, показываем как пришёл — выдуманная маска хуже.
 */
export const formatRussianPhone = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '';

    const digits = toRussianPhoneDigits(value);

    if (digits.length !== PHONE_DIGITS_WITHOUT_COUNTRY_CODE + 1 || digits[0] !== '7') {
        return String(value);
    }

    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
};

/**
 * Обработчик вставки для полей телефона под маской.
 *
 * Перехватываем `paste`, а не пользуемся `prepare` у IMask: там хук вызывается
 * посимвольно (`_appendChar` → `doPrepare`), поэтому целиком вставленный номер
 * он не видит и код страны распознать не может.
 *
 * Значение подставляем через нативное событие `input` — так маска сама
 * переформатирует его и сообщит об изменении наружу.
 */
export const handleRussianPhonePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    const prepared = prepareRussianPhoneInput(event.clipboardData.getData('text'));
    if (!prepared) return;

    event.preventDefault();

    const input = event.currentTarget;
    input.value = prepared;
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Подключает обрезку кода страны к полю телефона под маской.
 *
 * Зачем в дополнение к `handleRussianPhonePaste`: событие `paste` возникает
 * только при обычной вставке (Ctrl+V, «Вставить» из контекстного меню). Когда
 * на телефоне номер подставляют из подсказки буфера обмена над клавиатурой или
 * автозаполнением, браузер вставляет текст **без** `paste` — наружу выходит
 * только `beforeinput`. Поэтому там обрезка не срабатывала и в поле уезжал
 * номер со сдвигом: из `+79161234567` получалось `(791)-612-34-56`.
 *
 * Отличаем вставку от набора по объёму: за раз приходит больше одной цифры —
 * значит это не ручной ввод, а подстановка целиком.
 *
 * @param forwardedRef ref, который нужен вызывающему для своих нужд — заполняем заодно
 * @returns ref-колбэк для поля
 */
export const useRussianPhoneInput = (forwardedRef?: MutableRefObject<InputRef | null>) => {
    const detachRef = useRef<(() => void) | null>(null);

    return useCallback(
        (instance: InputRef | null) => {
            detachRef.current?.();
            detachRef.current = null;

            if (forwardedRef) forwardedRef.current = instance;

            const input = instance?.input;
            if (!input) return;

            const handleBeforeInput = (event: InputEvent) => {
                // При `insertFromPaste` спецификация кладёт текст в `dataTransfer`,
                // при подстановке из подсказки — в `data`
                const inserted = event.data ?? event.dataTransfer?.getData('text') ?? '';
                const digits = inserted.replace(/\D/g, '');

                if (digits.length < 2) return;

                const prepared = prepareRussianPhoneInput(inserted);
                if (prepared === digits) return;

                event.preventDefault();
                input.value = prepared;
                input.dispatchEvent(new Event('input', { bubbles: true }));
            };

            input.addEventListener('beforeinput', handleBeforeInput);
            detachRef.current = () => input.removeEventListener('beforeinput', handleBeforeInput);
        },
        [forwardedRef]
    );
};
